import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { enqueueSnackbar } from 'notistack';
import { BOOKING_STATUS, CLINIC_FLOW_CUSTOM_BOOKING_STATUSES, type AppConfigRoot } from '@a1/shared';

import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import { useGetEntityName } from 'hooks-ts/useGetEntity';
import { setAppConfigData } from 'store/features/appConfigSlice';
import { bookingStatuses } from 'constants/bookings';
import ConfirmationDialog from 'components-ts/dialogs/ConfirmationDialog';
import CustomStatusRow from './CustomStatusRow';
import type { BookingCustomStatusDraft, BookingCustomStatusSetup, BookingStatusSettingsDraft } from './types';

const NEW_CUSTOM_STATUS_COLOR = '#FFFFFF';

const customStatusGroups = [
  {
    systemStatus: BOOKING_STATUS.UPCOMING,
    label: bookingStatuses.Upcoming.label
  },
  {
    systemStatus: BOOKING_STATUS.COMPLETED,
    label: bookingStatuses.Completed.label
  },
  {
    systemStatus: BOOKING_STATUS.DID_NOT_ATTEND,
    label: bookingStatuses.DidNotAttend.label
  },
  {
    systemStatus: BOOKING_STATUS.CANCELED,
    label: bookingStatuses.Canceled.label
  }
];

const toDrafts = (bookingCustomStatusSetup: BookingCustomStatusSetup) => ({
  customStatusDrafts: bookingCustomStatusSetup.customStatuses.map((customStatus) => ({
    id: customStatus.id,
    draftKey: String(customStatus.id),
    name: customStatus.name,
    color: customStatus.color,
    systemStatus: customStatus.systemStatus,
    bookingCount: customStatus.bookingCount
  })),
  settingsDraft: {
    showCustomStatusesOnCalendar: bookingCustomStatusSetup.settings.showCustomStatusesOnCalendar
  }
});

interface BookingStatusesDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const BookingStatusesDialog = ({ open, onClose, onSaved }: BookingStatusesDialogProps) => {
  const dispatch = useDispatch();
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);
  const bookingEntityName = useGetEntityName('booking');
  const bookingsEntityName = useGetEntityName('bookings');

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customStatusDrafts, setCustomStatusDrafts] = useState<BookingCustomStatusDraft[]>([]);
  const [settingsDraft, setSettingsDraft] = useState<BookingStatusSettingsDraft>({ showCustomStatusesOnCalendar: true });
  const [newDraftCount, setNewDraftCount] = useState(0);
  const [pendingTemplateDrafts, setPendingTemplateDrafts] = useState<BookingCustomStatusDraft[] | null>(null);
  const [pendingDeleteDraft, setPendingDeleteDraft] = useState<BookingCustomStatusDraft | null>(null);

  const showErrorSnackbar = useCallback((message: string) => {
    enqueueSnackbar({ variant: 'error', message, anchorOrigin: { vertical: 'top', horizontal: 'right' } });
  }, []);

  const loadBookingCustomStatusSetup = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await callAzureFunction<BookingCustomStatusSetup>({ url: 'booking-custom-statuses', method: 'get' });
      const loadedDrafts = toDrafts(response.data);
      setCustomStatusDrafts(loadedDrafts.customStatusDrafts);
      setSettingsDraft(loadedDrafts.settingsDraft);
    } catch (error) {
      showErrorSnackbar(handleError(error));
    } finally {
      setIsLoading(false);
    }
  }, [showErrorSnackbar]);

  useEffect(() => {
    if (open) {
      loadBookingCustomStatusSetup();
    }
  }, [open, loadBookingCustomStatusSetup]);

  const nextDraftKey = () => {
    const draftKey = `new-${newDraftCount}`;
    setNewDraftCount((currentCount) => currentCount + 1);
    return draftKey;
  };

  const updateDraft = (draftKey: string, changes: Partial<BookingCustomStatusDraft>) => {
    setCustomStatusDrafts((currentDrafts) =>
      currentDrafts.map((customStatusDraft) =>
        customStatusDraft.draftKey === draftKey ? { ...customStatusDraft, ...changes } : customStatusDraft
      )
    );
  };

  const moveDraft = (draftKey: string, direction: -1 | 1) => {
    setCustomStatusDrafts((currentDrafts) => {
      const movingDraft = currentDrafts.find((customStatusDraft) => customStatusDraft.draftKey === draftKey);
      if (!movingDraft) {
        return currentDrafts;
      }
      const groupDrafts = currentDrafts.filter((customStatusDraft) => customStatusDraft.systemStatus === movingDraft.systemStatus);
      const groupIndex = groupDrafts.indexOf(movingDraft);
      const swapDraft = groupDrafts[groupIndex + direction];
      if (!swapDraft) {
        return currentDrafts;
      }
      return currentDrafts.map((customStatusDraft) => {
        if (customStatusDraft === movingDraft) {
          return swapDraft;
        }
        if (customStatusDraft === swapDraft) {
          return movingDraft;
        }
        return customStatusDraft;
      });
    });
  };

  const addDraft = (systemStatus: string) => {
    setCustomStatusDrafts((currentDrafts) => [
      ...currentDrafts,
      { id: null, draftKey: nextDraftKey(), name: 'New status', color: NEW_CUSTOM_STATUS_COLOR, systemStatus, bookingCount: 0 }
    ]);
  };

  const removeDraft = (removedDraft: BookingCustomStatusDraft) => {
    setCustomStatusDrafts((currentDrafts) =>
      currentDrafts.filter((customStatusDraft) => customStatusDraft.draftKey !== removedDraft.draftKey)
    );
  };

  const requestDelete = (customStatusDraft: BookingCustomStatusDraft) => {
    if (customStatusDraft.bookingCount > 0) {
      setPendingDeleteDraft(customStatusDraft);
      return;
    }
    removeDraft(customStatusDraft);
  };

  const requestTemplate = (templateDrafts: BookingCustomStatusDraft[]) => {
    if (customStatusDrafts.length > 0) {
      setPendingTemplateDrafts(templateDrafts);
      return;
    }
    applyTemplate(templateDrafts);
  };

  const applyTemplate = (templateDrafts: BookingCustomStatusDraft[]) => {
    setCustomStatusDrafts(templateDrafts);
  };

  const buildClinicFlowDrafts = () =>
    CLINIC_FLOW_CUSTOM_BOOKING_STATUSES.map((templateStatus, templateIndex) => ({
      id: null,
      draftKey: `template-${newDraftCount}-${templateIndex}`,
      name: templateStatus.name,
      color: templateStatus.color,
      systemStatus: templateStatus.systemStatus,
      bookingCount: 0
    }));

  const hasBlankName = useMemo(() => customStatusDrafts.some((customStatusDraft) => !customStatusDraft.name.trim()), [customStatusDrafts]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await callAzureFunction<BookingCustomStatusSetup>({
        url: 'booking-custom-statuses',
        method: 'put',
        data: {
          customStatuses: customStatusDrafts.map(({ id, draftKey, name, color, systemStatus }) => ({
            id,
            draftKey,
            name,
            color,
            systemStatus
          })),
          settings: settingsDraft
        }
      });

      dispatch(setAppConfigData({ data: { ...appConfig, bookingStatuses: response.data.settings } }));
      enqueueSnackbar({
        variant: 'success',
        message: `${bookingEntityName} statuses saved`,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
      onSaved?.();
      onClose();
    } catch (error) {
      showErrorSnackbar(handleError(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography component="span" variant="h3">{`${bookingEntityName} statuses and colours`}</Typography>
          <IconButton onClick={onClose} disabled={isSaving} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography fontWeight={600}>Show custom statuses on the calendar</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Turn off to go back to the system colours. Your custom statuses are kept.
                  </Typography>
                </Box>
                <Checkbox
                  checked={settingsDraft.showCustomStatusesOnCalendar}
                  onChange={(changeEvent) =>
                    setSettingsDraft((currentSettings) => ({
                      ...currentSettings,
                      showCustomStatusesOnCalendar: changeEvent.target.checked
                    }))
                  }
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                  <Typography fontWeight={600}>System status</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`${customStatusGroups.map((customStatusGroup) => customStatusGroup.label).join(', ')}. These are fixed and run reminders, invoices and workflow automations.`}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                  <Typography fontWeight={600}>Custom status</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your own steps, like Arrived or Pending GP. Each sits under one system status and sets the label and colour staff see.
                    Switching between custom statuses in the same group runs nothing.
                  </Typography>
                </Paper>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Start from
                </Typography>
                <Button variant="outlined" size="small" onClick={() => requestTemplate(buildClinicFlowDrafts())}>
                  Clinic flow template
                </Button>
                <Button variant="outlined" size="small" onClick={() => requestTemplate([])}>
                  System statuses only
                </Button>
              </Stack>

              {customStatusGroups.map((customStatusGroup) => {
                const groupDrafts = customStatusDrafts.filter(
                  (customStatusDraft) => customStatusDraft.systemStatus === customStatusGroup.systemStatus
                );

                return (
                  <Paper key={customStatusGroup.systemStatus} variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontWeight={700}>{customStatusGroup.label}</Typography>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ border: 1, borderColor: 'divider', borderRadius: 4, px: 1, py: 0.25 }}
                        >
                          <LockOutlinedIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">System status</Typography>
                        </Stack>
                      </Stack>
                    </Box>

                    <Stack spacing={1} sx={{ p: 2 }}>
                      {groupDrafts.map((customStatusDraft, groupIndex) => (
                        <CustomStatusRow
                          key={customStatusDraft.draftKey}
                          customStatus={customStatusDraft}
                          canMoveUp={groupIndex > 0}
                          canMoveDown={groupIndex < groupDrafts.length - 1}
                          onMoveUp={() => moveDraft(customStatusDraft.draftKey, -1)}
                          onMoveDown={() => moveDraft(customStatusDraft.draftKey, 1)}
                          onRename={(name) => updateDraft(customStatusDraft.draftKey, { name })}
                          onRecolor={(color) => updateDraft(customStatusDraft.draftKey, { color })}
                          onDelete={() => requestDelete(customStatusDraft)}
                        />
                      ))}
                      <Box>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addDraft(customStatusGroup.systemStatus)}>
                          {`Add a status under ${customStatusGroup.label}`}
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleSave} loading={isSaving} disabled={isLoading || hasBlankName}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(pendingTemplateDrafts)}
        title="Replace custom statuses?"
        message={`This replaces your current custom statuses. ${bookingsEntityName} using a removed status go back to their system status when you click Done.`}
        confirmText="Replace"
        cancelText="Cancel"
        onConfirm={() => {
          if (pendingTemplateDrafts) {
            applyTemplate(pendingTemplateDrafts);
          }
          setPendingTemplateDrafts(null);
        }}
        onCancel={() => setPendingTemplateDrafts(null)}
      />

      <ConfirmationDialog
        open={Boolean(pendingDeleteDraft)}
        title="Delete custom status?"
        message={`${pendingDeleteDraft?.bookingCount ?? 0} ${bookingsEntityName.toLowerCase()} use "${pendingDeleteDraft?.name ?? ''}". They go back to their system status when you click Done.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => {
          if (pendingDeleteDraft) {
            removeDraft(pendingDeleteDraft);
          }
          setPendingDeleteDraft(null);
        }}
        onCancel={() => setPendingDeleteDraft(null)}
      />
    </>
  );
};

export default BookingStatusesDialog;
